import type { OrganizationId } from '@moment/horizon-data-access/brands'
import type {
  ContextListResponse,
  EntityDetailResponse,
  EntityListResponse,
  MetricsRequest,
  MetricsResponse,
  QueryRequest,
  QueryResponse,
} from '@moment/horizon-data-access/platform'
import type { QueryKey } from '@tanstack/react-query'

/** The organization a private data access reads, once a page has chosen one. */
export type PrivateOrganization =
  | { readonly status: 'none' }
  | { readonly status: 'selected'; readonly organizationId: OrganizationId }

/**
 * Which SORM API a data access reaches. The platform API reads the
 * organization from the login session; a private data access is fixed to one
 * {@link PrivateOrganization} for its whole life.
 */
export type SormDataAccessScope =
  | { readonly api: 'platform' }
  | { readonly api: 'private'; readonly organization: PrivateOrganization }

/**
 * The segments a scope adds to every SORM cache key, after the key's name.
 * Platform adds none, so platform keys equal the unscoped keys.
 */
export function sormScopeKey(
  scope: SormDataAccessScope,
):
  | readonly []
  | readonly ['sorm-private', 'none']
  | readonly ['sorm-private', 'selected', OrganizationId] {
  switch (scope.api) {
    case 'platform':
      return []
    case 'private':
      switch (scope.organization.status) {
        case 'none':
          return ['sorm-private', 'none']
        case 'selected':
          return ['sorm-private', 'selected', scope.organization.organizationId]
        default:
          return scope.organization satisfies never
      }
    default:
      return scope satisfies never
  }
}

interface SormRequestOptions {
  readonly signal?: AbortSignal
}

/**
 * The SORM API a consumer sends its requests through.
 *
 * Every operation resolves with the response body. It rejects with a
 * `HorizonError` for a non-2xx response. The platform data access rejects
 * with an `AbortError` when the caller's signal aborts; a session-bound
 * private data access ignores the caller's signal and rejects with an
 * `AbortError` once its session ends.
 */
export interface SormDataAccess {
  readonly scope: SormDataAccessScope
  query(
    request: QueryRequest,
    options: SormRequestOptions & {
      /** Observability label sent as `?qn=`; see `executeSormQuery`. */
      readonly queryName: string
    },
  ): Promise<QueryResponse>
  metrics(
    request: MetricsRequest,
    options: SormRequestOptions & {
      /** Observability label sent as `?qn=`, as on {@link SormDataAccess.query}. */
      readonly queryName: string
    },
  ): Promise<MetricsResponse>
  getEntity(
    entityName: string,
    options: SormRequestOptions,
  ): Promise<EntityDetailResponse>
  listEntities(
    options: SormRequestOptions & {
      /** Only this query context's entities; the server defaults it. */
      readonly context?: string
    },
  ): Promise<EntityListResponse>
  getContexts(options: SormRequestOptions): Promise<ContextListResponse>
}

/** The only way to build a SORM cache key, so none can leave the scope out. */
export function sormCacheKey(
  dataAccess: SormDataAccess,
  name: string,
  ...rest: readonly unknown[]
): QueryKey {
  return [name, ...sormScopeKey(dataAccess.scope), ...rest]
}
